import { useEffect } from 'react';

import { getTenantUnits } from '@core/configuration';
import type { Address } from '@core/types/api/Address/Address';
import type Enums from '@core/types/api/Enums';
import { adjustDuration, roundDurationToMinutes } from '@core/utils/dateHelpers';
import { hasValue } from '@core/utils/equality';

import { useSlabQuery } from '../useSlabQuery';
import { usePlantDistances } from './usePlantDistances';

/** A single plant "row" in the form, normalized so the shared engine doesn't
 * need to know how the caller's formik shape lays out its plant sections.
 * TPath is the caller's path union, so `setPlantFields` can typecheck the
 * field paths it builds instead of accepting any string. */
export type PlantDistanceSection<TPath extends string = string> = {
  plantId: string | null;
  /** Formik path to the plant section, e.g. `productsByPlants.0`. */
  formikPath: TPath;
  products?: Array<{ product?: { id?: string | null } | null }> | undefined;
};

/** Values a caller needs to write onto the plant section itself. The field
 * names differ between forms (Quote vs Project), so the caller owns the writes. */
export type PlantDistanceFieldValues = {
  distanceInUnit: number | undefined;
  driveTimeISO: string | null;
  /** Length unit enum used for the distance. */
  unit: Enums.Unit;
};

type UsePlantDistanceCalculationParams<TPath extends string> = {
  /** Destination address to measure every selected plant against. */
  effectiveAddress: Address | undefined;
  /** All plant rows in the form, from any number of sections. */
  plantSections: PlantDistanceSection<TPath>[];
  setFieldValue: (field: string, value: unknown) => void;
  /** Writes the plant-level distance fields, whose names vary per form. */
  setPlantFields: (formikPath: TPath, values: PlantDistanceFieldValues) => void;
};

// Shared engine behind the Quote/Project `usePlantDistanceCalculation` hooks:
// listens to the destination address and selected plants, fetches the distances,
// and pushes the results into formik (both the plant sections and the already
// selected products' delivery costs).
export const usePlantDistanceCalculationShared = <TPath extends string>({
  effectiveAddress,
  plantSections,
  setFieldValue,
  setPlantFields,
}: UsePlantDistanceCalculationParams<TPath>): void => {
  const { data: rawPlantList } = useSlabQuery('GET plants', {});

  const selectedPlantIds = plantSections
    .map((section) => section.plantId)
    .filter((id) => hasValue(id)) as string[];

  const selectedPlants = rawPlantList?.items?.filter((p) => selectedPlantIds.includes(p.id));

  const { distanceByPlantId } = usePlantDistances({
    address: effectiveAddress,
    extraPlants: selectedPlants,
  });

  useEffect(() => {
    if (!selectedPlants) {
      return;
    }

    plantSections.forEach(({ plantId, formikPath, products }) => {
      if (!plantId || !distanceByPlantId.has(plantId)) return;
      const selectedPlant = selectedPlants.find((p) => p.id === plantId);
      if (!selectedPlant) return;

      const distance = distanceByPlantId.get(plantId) ?? null;
      const unit = selectedPlant.deliveryCosts.driveDistanceUnit ?? getTenantUnits().length;

      const distanceInUnit = distance?.distance?.toUnit(unit);
      const driveTime = distance?.duration?.value;
      const driveTimeISO = driveTime ? roundDurationToMinutes(driveTime).toISO() : null;
      // Adjust the rounded drive time, not the raw one, so the "One-way trip
      // time" we display is the adjustment of the drive time we actually store.
      const adjustedTime = driveTime
        ? adjustDuration(
            roundDurationToMinutes(driveTime),
            selectedPlant.deliveryCosts.drivingTimeAdjustmentPercentage,
          ).toISO()
        : null;

      setPlantFields(formikPath, { distanceInUnit, driveTimeISO, unit });

      // Propagate the new drive time/distance down to every already-selected product
      // on this plant. Blank rows (no product selected) are skipped — their drive time
      // is populated by onProductSelected via getPlantDistanceAndDriveTime.
      products?.forEach((product, productIndex) => {
        if (!product.product?.id) return;

        const productPath = `${formikPath}.products.${productIndex}`;

        setFieldValue(`${productPath}.deliveryCosts.driveDistanceUnit`, unit);
        setFieldValue(`${productPath}.deliveryCosts.driveToJobTime`, driveTimeISO);
        setFieldValue(`${productPath}.deliveryCosts.driveToPlantTime`, driveTimeISO);
        setFieldValue(`${productPath}.deliveryCosts.driveDistanceToJob`, distanceInUnit ?? null);
        setFieldValue(`${productPath}.deliveryCosts.driveDistanceToPlant`, distanceInUnit ?? null);
        setFieldValue(
          `${productPath}.deliveryCosts.displayOnly.adjustedDriveToJobTime`,
          adjustedTime,
        );
        setFieldValue(
          `${productPath}.deliveryCosts.displayOnly.adjustedDriveToPlantTime`,
          adjustedTime,
        );
      });
    });
  }, [distanceByPlantId, rawPlantList, selectedPlantIds.join(',')]);
};
